import React, { useState, useEffect } from "react";
import { LuPackage, LuClipboardList, LuUsers, LuMail } from "react-icons/lu";
import { db } from "../utils/firebase";
import { collection, getDocs } from "firebase/firestore";
import ProductsTable from "./ProductsTable";
import UsersTable from "./UsersTable";
import OrdersTable from "./OrdersTable";
import MessagesTable from "./MessagesTable";

const TABS = [
  { id: "products", label: "Products", icon: <LuPackage size={16} /> },
  { id: "orders",   label: "Orders",   icon: <LuClipboardList size={16} /> },
  { id: "users",    label: "Users",    icon: <LuUsers size={16} /> },
  { id: "messages", label: "Messages", icon: <LuMail size={16} /> },
];

export default function AdminDashboard() {
  const [activeTab, setActiveTab] = useState("products");
  const [stats, setStats]         = useState({ products: 0, orders: 0, users: 0, messages: 0 });

  useEffect(() => {
    (async () => {
      const [p, o, u, m] = await Promise.all([
        getDocs(collection(db, "products")),
        getDocs(collection(db, "orders")),
        getDocs(collection(db, "users")),
        getDocs(collection(db, "contactMessages")),
      ]);
      setStats({ products: p.size, orders: o.size, users: u.size, messages: m.size });
    })();
  }, []);

  const STAT_CARDS = [
    { label: "Total Products",   value: stats.products, color: "text-brand-amber", icon: <LuPackage size={20} /> },
    { label: "Total Orders",     value: stats.orders,   color: "text-blue-400",    icon: <LuClipboardList size={20} /> },
    { label: "Registered Users", value: stats.users,    color: "text-green-400",   icon: <LuUsers size={20} /> },
    { label: "Support Messages", value: stats.messages, color: "text-purple-400",  icon: <LuMail size={20} /> },
  ];

  return (
    <div className="min-h-screen bg-background font-sans">
      {/* ── Header ──────────────────────────────────────────── */}
      <div className="bg-brand-navy pt-28 pb-10 px-4 sm:px-8">
        <div className="max-w-screen-xl mx-auto">
          <div className="flex items-center gap-3 mb-1">
            <span className="text-xs font-bold text-brand-amber uppercase tracking-widest bg-brand-amber/10 px-3 py-1 rounded-full border border-brand-amber/20">
              Admin Panel
            </span>
          </div>
          <h1 className="text-3xl font-black text-white tracking-tight">Admin Dashboard</h1>
          <p className="text-slate-400 text-sm mt-1">Manage products, orders, and wholesale accounts.</p>

          {/* Stat cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-8">
            {STAT_CARDS.map((s) => (
              <div key={s.label} className="bg-white/5 border border-white/10 rounded-xl p-5 flex items-center gap-4">
                <div className={`${s.color} opacity-80`}>{s.icon}</div>
                <div>
                  <p className="text-2xl font-black text-white">{s.value}</p>
                  <p className="text-xs text-slate-400 font-medium">{s.label}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Tabs ────────────────────────────────────────────── */}
      <div className="bg-brand-navy border-b border-white/10">
        <div className="max-w-screen-xl mx-auto flex gap-1 overflow-x-auto scrollbar-none px-4 sm:px-8">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-3 sm:px-5 py-4 text-sm font-bold transition-colors border-b-2 whitespace-nowrap flex-shrink-0 ${
                activeTab === tab.id
                  ? "border-brand-amber text-brand-amber"
                  : "border-transparent text-slate-400 hover:text-white"
              }`}
            >
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* ── Content ─────────────────────────────────────────── */}
      <div className="max-w-screen-xl mx-auto px-4 sm:px-8 py-10">
        {activeTab === "products" && (
          <ProductsTable
            onAdd={()    => setStats((prev) => ({ ...prev, products: prev.products + 1 }))}
            onDelete={()  => setStats((prev) => ({ ...prev, products: Math.max(0, prev.products - 1) }))}
          />
        )}
        {activeTab === "orders"   && <OrdersTable />}
        {activeTab === "users"    && <UsersTable />}
        {activeTab === "messages" && (
          <MessagesTable onDelete={() => setStats((prev) => ({ ...prev, messages: Math.max(0, prev.messages - 1) }))} />
        )}
      </div>
    </div>
  );
}
